import { EntityLinks, Goal, ID, IDPrefixes } from '@griply/types';

import { getGoalParentIds } from './goals/get-goal-parents';
import { getLinkedIds } from './links/get-linked-ids';
import { createPrefixId } from './prefix-id';

export type GetGoalLifeAreasOptions = {
  goalsById: Map<ID, Goal>;
  links?: EntityLinks;
  // Callers resolving in a loop should pass one map — the inheritance walk is otherwise
  // repeated per goal, and fanning out over multiple parents makes that exponential.
  cache?: Map<ID, ID[]>;
};

export const buildGoalsById = (goals: Goal[]): Map<ID, Goal> =>
  new Map(goals.map((goal) => [goal.id, goal]));

// Gated on the flag, never on document presence: a parent write creates the same document.
// Structural so a caller holding a projection can resolve its life areas too.
export type GoalLifeAreaSource = {
  id: ID;
  lifeAreaId?: ID | null;
  lifeAreaLinksMigrated?: boolean | null;
};

export const getDirectGoalLifeAreaIds = (
  goal: GoalLifeAreaSource,
  links?: EntityLinks,
): ID[] => {
  if (!goal.lifeAreaLinksMigrated) {
    return goal.lifeAreaId ? [goal.lifeAreaId] : [];
  }

  return links
    ? getLinkedIds(
        links,
        createPrefixId(goal.id, IDPrefixes.Goal),
        IDPrefixes.LifeArea,
      )
    : [];
};

// Direct links win outright; a goal with none inherits its nearest ancestor's set.
export const getGoalLifeAreas = (
  goal: Goal,
  { goalsById, links, cache }: GetGoalLifeAreasOptions,
  visited: Set<ID> = new Set(),
): ID[] => {
  const cached = cache?.get(goal.id);
  if (cached) {
    return cached;
  }

  const resolve = (): ID[] => {
    const directIds = getDirectGoalLifeAreaIds(goal, links);
    // The first parent link (the frozen chain's direct parent for an unmigrated goal) is the one inherited from.
    const parentIds = getGoalParentIds(goal, { links });

    if (directIds.length || !parentIds.length) {
      return directIds;
    }

    if (visited.has(goal.id)) {
      return [];
    }
    visited.add(goal.id);

    const parentGoal = goalsById.get(parentIds[0]);

    return parentGoal
      ? getGoalLifeAreas(parentGoal, { goalsById, links, cache }, visited)
      : [];
  };

  const result = resolve();
  cache?.set(goal.id, result);

  return result;
};

export const getGoalLifeArea = (
  goal: Goal,
  options: GetGoalLifeAreasOptions,
): ID | undefined => getGoalLifeAreas(goal, options)[0];

export type GetGoalTopLevelLifeAreasOptions = GetGoalLifeAreasOptions & {
  // Whether the goal already renders nested under a present parent.
  isNested: boolean;
};

// A nested goal's legacy scalar is the copy a parent write cascaded down, never an own link.
export const getGoalTopLevelLifeAreas = (
  goal: Goal,
  { isNested, ...options }: GetGoalTopLevelLifeAreasOptions,
): ID[] => {
  if (!isNested) {
    return getGoalLifeAreas(goal, options);
  }

  return goal.lifeAreaLinksMigrated
    ? getDirectGoalLifeAreaIds(goal, options.links)
    : [];
};
